import type { AiChatSourceMessage } from '../types/aiChatSource'

export const MAX_MESSAGES_PER_CHAT_CTX = 500
export const MAX_TOKENS_PER_CHAT_CTX = 50_000
export const CHARS_PER_TOKEN = 4
export const MAX_CHARS_PER_CHAT_CTX = MAX_TOKENS_PER_CHAT_CTX * CHARS_PER_TOKEN // 200,000 chars

export type ChatCapResult = {
  messages: AiChatSourceMessage[]
  wasTruncated: boolean
  reason?: 'message_count' | 'token_budget'
  totalTokens: number
  originalCount: number
}

/**
 * Enforces hard caps on chat context selection:
 * - At most 500 messages per context.
 * - At most ~50,000 tokens (~200k chars) per context.
 * When limits are exceeded, truncates newest-first (keeps the most recent messages).
 */
export const capChatSelection = (
  messages: AiChatSourceMessage[],
): ChatCapResult => {
  const originalCount = messages.length
  if (originalCount === 0) {
    return {
      messages: [],
      wasTruncated: false,
      totalTokens: 0,
      originalCount: 0,
    }
  }

  // Sort chronological (oldest to newest) to slice from end
  const sorted = [...messages].sort((a, b) => a.timestampMs - b.timestampMs)

  let wasTruncated = false
  let reason: 'message_count' | 'token_budget' | undefined

  let working = sorted
  if (working.length > MAX_MESSAGES_PER_CHAT_CTX) {
    working = working.slice(-MAX_MESSAGES_PER_CHAT_CTX)
    wasTruncated = true
    reason = 'message_count'
  }

  // Now calculate tokens from newest to oldest
  let totalChars = 0
  const kept: AiChatSourceMessage[] = []

  for (let i = working.length - 1; i >= 0; i--) {
    const msg = working[i]
    const msgChars = (msg.textContent || '').length
    if (totalChars + msgChars > MAX_CHARS_PER_CHAT_CTX) {
      wasTruncated = true
      if (!reason) reason = 'token_budget'
      break
    }
    totalChars += msgChars
    kept.unshift(msg)
  }

  const totalTokens = Math.ceil(totalChars / CHARS_PER_TOKEN)

  return {
    messages: kept,
    wasTruncated,
    reason,
    totalTokens,
    originalCount,
  }
}
